import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowRight, ArrowLeft, Check, Upload, X, 
  ShoppingBag, Globe, Workflow, Brain, Box, LineChart,
  Mail, Phone, Building, FileText, Calendar, DollarSign, UserCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { supabase } from '../../lib/supabase';

const NewQuoteRequest = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { language } = useLanguage();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    serviceType: '',
    fullName: '',
    email: '',
    phone: '',
    companyName: '',
    budgetRange: '',
    targetDate: '',
    description: '',
    attachments: []
  });

  // Auth guard and initialization
  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/auth/login');
      } else {
        // Set email from authenticated user
        setFormData(prev => ({
          ...prev,
          email: user.email || ''
        }));
      }
    }
  }, [user, authLoading, navigate]);

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">
            {language === 'tr' ? 'Yükleniyor...' : 'Loading...'}
          </p>
        </div>
      </div>
    );
  }

  // Don't render if no user
  if (!user) {
    return null;
  }

  const services = [
    { id: 'ecommerce', icon: ShoppingBag, nameEN: 'E-Commerce & Marketplace', nameTR: 'E-Ticaret & Marketplace' },
    { id: 'web-app', icon: Globe, nameEN: 'Web & App Development', nameTR: 'Web & Uygulama Geliştirme' },
    { id: 'automation', icon: Workflow, nameEN: 'Automation & Integration', nameTR: 'Otomasyon & Entegrasyon' },
    { id: 'ai', icon: Brain, nameEN: 'AI Agents & Chatbots', nameTR: 'AI Ajanları & Chatbotlar' },
    { id: '3d', icon: Box, nameEN: '3D Modeling & Printing', nameTR: '3D Modelleme & Baskı' },
    { id: 'marketing', icon: LineChart, nameEN: 'Performance Marketing', nameTR: 'Performans Pazarlama' }
  ];

  const budgetRanges = [
    { value: '5k-10k', labelEN: '$5,000 - $10,000', labelTR: '$5,000 - $10,000' },
    { value: '10k-25k', labelEN: '$10,000 - $25,000', labelTR: '$10,000 - $25,000' },
    { value: '25k-50k', labelEN: '$25,000 - $50,000', labelTR: '$25,000 - $50,000' },
    { value: '50k+', labelEN: '$50,000+', labelTR: '$50,000+' },
    { value: 'flexible', labelEN: 'Flexible / Quote-Based', labelTR: 'Esnek / Teklife Göre' }
  ];

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    setFormData({ ...formData, attachments: files });
  };

  const removeFile = (index) => {
    const newFiles = formData.attachments.filter((_, i) => i !== index);
    setFormData({ ...formData, attachments: newFiles });
  };

  const handleSubmit = async () => {
    if (!user) {
      setError(language === 'tr' ? 'Oturum süresi doldu. Lütfen tekrar giriş yapın.' : 'Session expired. Please login again.');
      setTimeout(() => navigate('/auth/login'), 2000);
      return;
    }

    setLoading(true);
    setError('');

    try {
      console.log('=== Quote Submission Debug ===');
      console.log('User ID:', user.id);
      console.log('User Email:', user.email);

      // Step 1: Ensure user has a profile (self-healing for users without profiles)
      // Using maybeSingle() instead of single() to avoid body stream issues
      const profileCheck = await supabase
        .from('profiles')
        .select('id')
        .eq('id', user.id)
        .maybeSingle();

      if (!profileCheck.data && !profileCheck.error) {
        // Profile doesn't exist - create it
        console.log('Profile not found, creating one...');
        await supabase
          .from('profiles')
          .insert({
            id: user.id,
            email: user.email,
            full_name: formData.fullName || user.user_metadata?.full_name || user.email.split('@')[0],
            role: 'customer'
          });
        console.log('Profile creation attempted');
      }

      // Step 2: Prepare insert data
      const insertData = {
        user_id: user.id,
        full_name: formData.fullName,
        email: formData.email,
        phone: formData.phone || null,
        company_name: formData.companyName || null,
        service_type: formData.serviceType,
        budget_range: formData.budgetRange || null,
        target_date: formData.targetDate || null,
        description: formData.description,
        status: 'new'
      };

      console.log('Insert Data:', insertData);

      // Step 3: Insert quote request - avoid .single() to prevent body stream issues
      const insertResult = await supabase
        .from('quote_requests')
        .insert(insertData)
        .select();

      if (insertResult.error) {
        console.error('=== Supabase Insert Error ===');
        console.error('Error:', insertResult.error);
        
        // Provide user-friendly error messages based on error type
        let userMessage = insertResult.error.message;
        
        if (insertResult.error.code === '23503') {
          // Foreign key violation - profile issue
          userMessage = language === 'tr' 
            ? 'Hesabınızla ilgili bir sorun var. Lütfen çıkış yapıp tekrar giriş yapın veya destek ekibiyle iletişime geçin.'
            : 'There is an issue with your account. Please sign out and sign in again, or contact support.';
        } else if (insertResult.error.code === '42501') {
          // RLS policy violation
          userMessage = language === 'tr'
            ? 'Bu işlem için yetkiniz yok. Lütfen tekrar giriş yapın.'
            : 'You do not have permission for this action. Please sign in again.';
        }
        
        throw new Error(userMessage);
      }

      console.log('=== Insert Success ===');
      console.log('Inserted Data:', insertResult.data);

      // Success - redirect to portal
      navigate('/portal?success=quote');
    } catch (err) {
      console.error('=== Submission Error ===', err);
      
      // Show error in UI
      const errorMessage = err.message || 'Unknown error';
      setError(
        language === 'tr' 
          ? `Talep gönderilemedi: ${errorMessage}` 
          : `Failed to submit: ${errorMessage}`
      );
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (step === 1 && !formData.serviceType) {
      setError(language === 'tr' ? 'Lütfen bir hizmet seçin' : 'Please select a service');
      return;
    }
    if (step === 2 && (!formData.fullName || !formData.email)) {
      setError(language === 'tr' ? 'Lütfen zorunlu alanları doldurun' : 'Please fill required fields');
      return;
    }
    if (step === 3 && !formData.description) {
      setError(language === 'tr' ? 'Lütfen proje açıklaması girin' : 'Please enter project description');
      return;
    }
    setError('');
    setStep(step + 1);
  };

  const prevStep = () => setStep(step - 1);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            {language === 'tr' ? 'Yeni Teklif Talebi' : 'New Quote Request'}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {language === 'tr' 
              ? 'Projeniz hakkında bilgi verin, size en kısa sürede dönüş yapalım' 
              : 'Tell us about your project and we\'ll get back to you soon'}
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-12">
          <div className="flex items-center justify-center">
            {[1, 2, 3, 4].map((s) => (
              <React.Fragment key={s}>
                <div className="flex flex-col items-center">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center font-semibold transition-all ${
                    s < step ? 'bg-green-500 text-white' :
                    s === step ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white' :
                    'bg-gray-200 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
                  }`}>
                    {s < step ? <Check className="w-6 h-6" /> : s}
                  </div>
                  <span className={`text-xs mt-2 font-medium ${
                    s === step ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'
                  }`}>
                    {language === 'tr' ? (
                      s === 1 ? 'Hizmet' : s === 2 ? 'Bilgiler' : s === 3 ? 'Detaylar' : 'Onay'
                    ) : (
                      s === 1 ? 'Service' : s === 2 ? 'Info' : s === 3 ? 'Details' : 'Review'
                    )}
                  </span>
                </div>
                {s < 4 && (
                  <div className={`w-24 h-1 mx-2 rounded ${
                    s < step ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-800'
                  }`}></div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl"
          >
            <p className="text-sm font-semibold text-red-800 dark:text-red-300 mb-2">
              {language === 'tr' ? 'Hata Oluştu:' : 'Error Occurred:'}
            </p>
            <pre className="text-xs text-red-700 dark:text-red-200 whitespace-pre-wrap max-h-40 overflow-y-auto bg-red-100 dark:bg-red-900/10 p-3 rounded">
              {error}
            </pre>
            <p className="text-xs text-red-600 dark:text-red-400 mt-2">
              {language === 'tr' 
                ? 'Lütfen browser konsolunu (F12) kontrol edin veya destek ekibiyle iletişime geçin.' 
                : 'Please check browser console (F12) or contact support.'}
            </p>
          </motion.div>
        )}

        {/* Form Content */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-xl p-8">
          <AnimatePresence mode="wait">
            {/* Step 1: Service Selection */}
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  {language === 'tr' ? 'Hangi hizmeti seçmek istersiniz?' : 'Which service do you need?'}
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {services.map((service) => {
                    const Icon = service.icon;
                    const isSelected = formData.serviceType === service.id;
                    return (
                      <button
                        key={service.id}
                        onClick={() => setFormData({ ...formData, serviceType: service.id })}
                        className={`p-6 rounded-xl border-2 text-left transition-all ${
                          isSelected
                            ? 'border-blue-600 dark:border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                            : 'border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'
                        }`}
                      >
                        <Icon className={`w-8 h-8 mb-3 ${
                          isSelected ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'
                        }`} />
                        <h3 className={`font-semibold mb-1 ${
                          isSelected ? 'text-blue-900 dark:text-blue-200' : 'text-gray-900 dark:text-white'
                        }`}>
                          {language === 'tr' ? service.nameTR : service.nameEN}
                        </h3>
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {/* Step 2: Contact Information */}
            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  {language === 'tr' ? 'İletişim Bilgileriniz' : 'Your Contact Information'}
                </h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {language === 'tr' ? 'Ad Soyad' : 'Full Name'} *
                    </label>
                    <div className="relative">
                      <UserCircle className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        value={formData.fullName}
                        onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                        className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none transition-colors"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {language === 'tr' ? 'E-posta' : 'Email'} *
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none transition-colors"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {language === 'tr' ? 'Telefon' : 'Phone'}
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none transition-colors"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {language === 'tr' ? 'Şirket' : 'Company'}
                    </label>
                    <div className="relative">
                      <Building className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        value={formData.companyName}
                        onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                        className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none transition-colors"
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 3: Project Details */}
            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  {language === 'tr' ? 'Proje Detayları' : 'Project Details'}
                </h2>
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {language === 'tr' ? 'Bütçe Aralığı' : 'Budget Range'}
                    </label>
                    <div className="relative">
                      <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <select
                        value={formData.budgetRange}
                        onChange={(e) => setFormData({ ...formData, budgetRange: e.target.value })}
                        className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none transition-colors"
                      >
                        <option value="">{language === 'tr' ? 'Seçiniz' : 'Select'}</option>
                        {budgetRanges.map((range) => (
                          <option key={range.value} value={range.value}>
                            {language === 'tr' ? range.labelTR : range.labelEN}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {language === 'tr' ? 'Hedef Tarih' : 'Target Date'}
                    </label>
                    <div className="relative">
                      <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="date"
                        value={formData.targetDate}
                        onChange={(e) => setFormData({ ...formData, targetDate: e.target.value })}
                        className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none transition-colors"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {language === 'tr' ? 'Proje Açıklaması' : 'Project Description'} *
                    </label>
                    <div className="relative">
                      <FileText className="absolute left-4 top-4 w-5 h-5 text-gray-400" />
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        rows={6}
                        className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-900 dark:text-white focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none transition-colors resize-none"
                        placeholder={language === 'tr' 
                          ? 'Projeniz hakkında detaylı bilgi verin...' 
                          : 'Tell us about your project in detail...'}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {language === 'tr' ? 'Dosya Ekle (Opsiyonel)' : 'Attach Files (Optional)'}
                    </label>
                    <div className="border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-xl p-6 text-center hover:border-blue-500 dark:hover:border-blue-400 transition-colors">
                      <input
                        type="file"
                        multiple
                        onChange={handleFileUpload}
                        className="hidden"
                        id="file-upload"
                      />
                      <label htmlFor="file-upload" className="cursor-pointer">
                        <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-gray-600 dark:text-gray-400 mb-1">
                          {language === 'tr' ? 'Dosya yüklemek için tıklayın' : 'Click to upload files'}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-500">
                          {language === 'tr' ? 'PDF, DOC, JPG, PNG (max 10MB)' : 'PDF, DOC, JPG, PNG (max 10MB)'}
                        </p>
                      </label>
                    </div>
                    {formData.attachments.length > 0 && (
                      <div className="mt-4 space-y-2">
                        {formData.attachments.map((file, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                            <span className="text-sm text-gray-700 dark:text-gray-300">{file.name}</span>
                            <button
                              onClick={() => removeFile(index)}
                              className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                            >
                              <X className="w-5 h-5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 4: Review */}
            {step === 4 && (
              <motion.div
                key="step4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  {language === 'tr' ? 'Bilgileri Kontrol Edin' : 'Review Your Information'}
                </h2>
                <div className="space-y-4">
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                      {language === 'tr' ? 'Seçilen Hizmet' : 'Selected Service'}
                    </h3>
                    <p className="text-gray-700 dark:text-gray-300">
                      {language === 'tr' 
                        ? services.find(s => s.id === formData.serviceType)?.nameTR 
                        : services.find(s => s.id === formData.serviceType)?.nameEN}
                    </p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                      {language === 'tr' ? 'İletişim Bilgileri' : 'Contact Information'}
                    </h3>
                    <p className="text-gray-700 dark:text-gray-300">{formData.fullName}</p>
                    <p className="text-gray-700 dark:text-gray-300">{formData.email}</p>
                    {formData.phone && <p className="text-gray-700 dark:text-gray-300">{formData.phone}</p>}
                    {formData.companyName && <p className="text-gray-700 dark:text-gray-300">{formData.companyName}</p>}
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                      {language === 'tr' ? 'Proje Detayları' : 'Project Details'}
                    </h3>
                    {formData.budgetRange && (
                      <p className="text-gray-700 dark:text-gray-300 mb-2">
                        <strong>{language === 'tr' ? 'Bütçe:' : 'Budget:'}</strong>{' '}
                        {language === 'tr' 
                          ? budgetRanges.find(b => b.value === formData.budgetRange)?.labelTR 
                          : budgetRanges.find(b => b.value === formData.budgetRange)?.labelEN}
                      </p>
                    )}
                    {formData.targetDate && (
                      <p className="text-gray-700 dark:text-gray-300 mb-2">
                        <strong>{language === 'tr' ? 'Hedef Tarih:' : 'Target Date:'}</strong>{' '}
                        {new Date(formData.targetDate).toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US')}
                      </p>
                    )}
                    <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{formData.description}</p>
                  </div>
                  {formData.attachments.length > 0 && (
                    <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                        {language === 'tr' ? 'Ekli Dosyalar' : 'Attached Files'}
                      </h3>
                      {formData.attachments.map((file, index) => (
                        <p key={index} className="text-gray-700 dark:text-gray-300">{file.name}</p>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200 dark:border-gray-800">
            <button
              onClick={prevStep}
              disabled={step === 1}
              className="inline-flex items-center px-6 py-3 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              {language === 'tr' ? 'Geri' : 'Back'}
            </button>
            {step < 4 ? (
              <button
                onClick={nextStep}
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-xl hover:shadow-blue-500/30 dark:hover:shadow-blue-500/50 transition-all font-semibold"
              >
                {language === 'tr' ? 'İleri' : 'Next'}
                <ArrowRight className="w-5 h-5 ml-2" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:shadow-xl hover:shadow-green-500/30 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  language === 'tr' ? 'Gönderiliyor...' : 'Submitting...'
                ) : (
                  <>
                    <Check className="w-5 h-5 mr-2" />
                    {language === 'tr' ? 'Talebi Gönder' : 'Submit Request'}
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewQuoteRequest;
